import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";
import { SPACING } from "../../constants/design/spacing";
import { LINKS } from "../../constants/links";
import ExternalLinkText from "../ExternalLinkText";
import { UptimeStatusEnum } from "./model";
import { StatusContainer, StatusIndicator } from "./style";

interface IUptimeMonitorProps {
  maintenanceTimestampSeconds: number;
}

function UptimeMonitor({ maintenanceTimestampSeconds }: IUptimeMonitorProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "UptimeMonitor.UptimeMonitor",
  });

  const maintenanceInProgress = Number(maintenanceTimestampSeconds)
    ? Number(maintenanceTimestampSeconds) * 1000 <= Date.now()
    : false;

  // Temporarily disabled to avoid client-side UptimeRobot getMonitors calls.
  const uptimeStatus = useMemo<UptimeStatusEnum | undefined>(
    () => undefined,
    []
  );

  const statusDescription = useMemo(() => {
    if (maintenanceInProgress) {
      return t("maintenance_in_progress");
    }
    switch (uptimeStatus) {
      case UptimeStatusEnum.DOWN:
        return t("systems_outage");
      case UptimeStatusEnum.SEEMS_DOWN:
        return t("partial_systems_outage");
      default:
        return t("systems_operational");
    }
  }, [maintenanceInProgress, t, uptimeStatus]);

  // With the UptimeRobot polling disabled, `uptimeStatus` is permanently
  // undefined and the switch above always falls through to its green
  // "Systems Operational" default — a claim this component cannot currently
  // back, and which sat in the footer while the risk panel was reporting that
  // its contract reads had failed. Render nothing unless there is something
  // real to say; the maintenance-window path still works.
  if (!maintenanceInProgress && uptimeStatus === undefined) return null;

  return (
    <StatusContainer>
      <StatusIndicator
        maintenanceInProgress={maintenanceInProgress}
        status={uptimeStatus}
      />
      <ExternalLinkText
        onClick={() => window.open(LINKS.uptimeRobot)}
        color={maintenanceInProgress ? COLORS.highlight.one : TEXT_COLORS.three}
        hoverColor={
          maintenanceInProgress ? COLORS.highlight.one : TEXT_COLORS.one
        }
        wrapperStyle={{
          marginLeft: `${SPACING.two}px`,
          fontSize: FONT_STYLE.body.four.fontSize,
          lineHeight: FONT_STYLE.body.four.lineHeight,
        }}
      >
        {statusDescription}
      </ExternalLinkText>
    </StatusContainer>
  );
}

export default UptimeMonitor;
